import React, { memo, lazy, Suspense } from "react";
import type * as maplibregl from "maplibre-gl";
import { Tooltip } from "@vector-im/compound-web";

import Spinner from "../elements/Spinner";

// Lazy-load Map so maplibre-gl is code-split, while keeping the Spinner
// inside the correctly-sized container (fixes spinner positioning).
const Map = lazy(() => import("./Map"));

export interface LocationMapTileProps {
    mapId: string;
    geoUri: string;
    tooltip?: string;
    /** CSS class applied to the `<Map>` element itself */
    mapClassName?: string;
    /** CSS class applied to the container `<div>` wrapping the map (and Tooltip anchor) */
    containerClassName?: string;
    onClick?: () => void;
    onError: (error: Error) => void;
    /** Render prop — receives the maplibre Map instance and returns the marker element(s) */
    renderMarker: (props: { map: maplibregl.Map }) => React.ReactNode;
}

/**
 * Shared map tile component used by both MLocationBody and SOSMessageBody.
 * Renders a Map wrapped in an optional Tooltip, delegating marker rendering to the caller.
 * Error state and click handling are owned by the parent.
 */
export const LocationMapTile: React.FC<LocationMapTileProps> = memo(
    ({ mapId, geoUri, tooltip, mapClassName, containerClassName, onClick, onError, renderMarker }) => {
        const mapElement = (
            <Suspense fallback={<Spinner />}>
                <Map
                    id={mapId}
                    centerGeoUri={geoUri}
                    onClick={onClick}
                    onError={onError}
                    className={mapClassName}
                >
                    {renderMarker}
                </Map>
            </Suspense>
        );

        if (tooltip) {
            return (
                <Tooltip label={tooltip}>
                    <div className={containerClassName}>{mapElement}</div>
                </Tooltip>
            );
        }

        return <div className={containerClassName}>{mapElement}</div>;
    },
);

export default LocationMapTile;
